'use client';

/**
 * OrganizationUsersTab
 *
 * User management tab for organization page.
 * Displays active users and pending invitations in a unified table view.
 */

import {
  Alert,
  AlertDescription,
  Avatar,
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Inline,
  Stack,
  Text,
} from '@cortex/ui';
import { MoreHorizontal, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useApi } from '@/hooks/use-api';
import { formatDate } from '@/lib/admin-utils';

import { EditUserRoleModal } from './EditUserRoleModal';
import { InviteUserModal } from './InviteUserModal';
import { RemoveUserDialog } from './RemoveUserDialog';

interface OrganizationUsersTabProps {
  organizationId: string;
}

/** User from API response */
interface User {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: 'user' | 'admin';
  organization_id: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  confirmed_at: string | null;
  invited_at: string | null;
}

/** Invitation from API response */
interface Invitation {
  id: string;
  email: string;
  role: string;
  token: string;
  invited_by: string | null;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  status: 'pending' | 'accepted' | 'expired';
}

/** Combined table row */
interface MemberRow {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: string;
  status: 'active' | 'pending' | 'expired';
  joinedOrInvitedAt: string;
  type: 'user' | 'invitation';
}

export function OrganizationUsersTab({
  organizationId,
}: OrganizationUsersTabProps) {
  const getApi = useApi();
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal visibility states
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberRow | null>(null);

  /** Fetch users and invitations */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const api = await getApi();

      // Fetch users
      const usersResponse = await api.admin.users.$get({
        query: { organizationId },
      });

      if (!usersResponse.ok) {
        throw new Error('Failed to fetch users');
      }

      const usersJson = await usersResponse.json();
      if ('data' in usersJson) {
        const usersData = usersJson.data as unknown as { items: User[] };
        setUsers(usersData.items || []);
      }

      // Fetch pending invitations
      const invitationsResponse = await api.invitations.index.$get({
        query: { organizationId, pendingOnly: 'false' },
      });

      if (!invitationsResponse.ok) {
        throw new Error('Failed to fetch invitations');
      }

      const invitationsJson = await invitationsResponse.json();
      if ('data' in invitationsJson) {
        setInvitations((invitationsJson.data as unknown as Invitation[]) || []);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load team members'
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId, getApi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Calculate days ago */
  const daysAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  /** Combine users and invitations into unified table rows */
  const combinedRows: MemberRow[] = [
    // Active users
    ...users.map((user) => ({
      id: user.id,
      email: user.email,
      name: user.full_name,
      avatarUrl: user.avatar_url,
      role: user.role,
      status: 'active' as const,
      joinedOrInvitedAt: user.created_at,
      type: 'user' as const,
    })),
    // Invitations (exclude accepted since they're now users)
    ...invitations
      .filter((inv) => inv.status !== 'accepted')
      .map((invitation) => ({
        id: invitation.id,
        email: invitation.email,
        name: null,
        avatarUrl: null,
        role: invitation.role,
        status: invitation.status as 'pending' | 'expired',
        joinedOrInvitedAt: invitation.created_at,
        type: 'invitation' as const,
      })),
  ];

  /** Get badge variant for role */
  const getRoleBadgeVariant = (role: string): 'default' | 'secondary' => {
    if (role === 'admin' || role === 'owner') return 'default';
    return 'secondary';
  };

  /** Get badge variant for status */
  const getStatusBadgeVariant = (
    status: string
  ): 'success' | 'warning' | 'destructive' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'expired':
        return 'destructive';
      default:
        return 'warning';
    }
  };

  /** Handle resend invitation */
  const handleResendInvitation = async (invitationId: string) => {
    try {
      const api = await getApi();
      const response = await api.invitations[':id'].resend.$post({
        param: { id: invitationId },
      });

      if (!response.ok) {
        throw new Error('Failed to resend invitation');
      }

      // Refetch data
      await fetchData();
    } catch {
      // Silently handle resend failure
    }
  };

  /** Handle revoke invitation */
  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      const api = await getApi();
      const response = await api.invitations[':id'].$delete({
        param: { id: invitationId },
      });

      if (!response.ok) {
        throw new Error('Failed to revoke invitation');
      }

      // Refetch data
      await fetchData();
    } catch {
      // Silently handle revoke failure
    }
  };

  /** Table columns */
  const columns = [
    {
      key: 'member',
      header: 'Member',
      sortable: true,
      render: (row: MemberRow) => (
        <Inline gap="sm">
          <Avatar
            {...(row.avatarUrl ? { src: row.avatarUrl } : {})}
            fallback={row.name || row.email}
            size="sm"
          />
          <Stack gap="none">
            <Text weight="medium">{row.name || row.email}</Text>
            {row.name && (
              <Text color="muted" size="sm">
                {row.email}
              </Text>
            )}
          </Stack>
        </Inline>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (row: MemberRow) => (
        <Badge variant={getRoleBadgeVariant(row.role)}>
          {row.role.charAt(0).toUpperCase() + row.role.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: MemberRow) => (
        <Badge variant={getStatusBadgeVariant(row.status)}>
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'joined',
      header: 'Joined',
      sortable: true,
      render: (row: MemberRow) => {
        if (row.status === 'active') {
          return (
            <Text size="sm" color="muted">
              {formatDate(row.joinedOrInvitedAt)}
            </Text>
          );
        } else {
          const days = daysAgo(row.joinedOrInvitedAt);
          return (
            <Text size="sm" color="muted">
              Invited{' '}
              {days === 0
                ? 'today'
                : `${days} ${days === 1 ? 'day' : 'days'} ago`}
            </Text>
          );
        }
      },
    },
  ];

  /** Row actions */
  const rowActions = (row: MemberRow) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreHorizontal className="h-4 w-4" />
          <Text as="span" className="sr-only">
            Open menu
          </Text>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {row.type === 'user' ? (
          <>
            <DropdownMenuItem
              onClick={() => {
                setSelectedMember(row);
                setEditRoleModalOpen(true);
              }}
            >
              Edit Role
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() => {
                setSelectedMember(row);
                setRemoveUserDialogOpen(true);
              }}
            >
              Remove from Organization
            </DropdownMenuItem>
          </>
        ) : (
          <>
            {row.status === 'pending' && (
              <DropdownMenuItem onClick={() => handleResendInvitation(row.id)}>
                Resend Invitation
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              variant="destructive"
              onClick={() => handleRevokeInvitation(row.id)}
            >
              Revoke Invitation
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const activeUsersCount = users.length;
  const pendingInvitationsCount = invitations.filter(
    (inv) => inv.status === 'pending'
  ).length;

  return (
    <Stack gap="md">
      {/* Header */}
      <Inline justify="between">
        <Stack gap="none">
          <Text weight="semibold">Team Members</Text>
          <Text color="muted" size="sm">
            {activeUsersCount} {activeUsersCount === 1 ? 'member' : 'members'}
            {pendingInvitationsCount > 0 &&
              `, ${pendingInvitationsCount} pending ${pendingInvitationsCount === 1 ? 'invitation' : 'invitations'}`}
          </Text>
        </Stack>
        <Button onClick={() => setInviteModalOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite Member
        </Button>
      </Inline>

      {/* Table */}
      <DataTable
        columns={columns}
        data={combinedRows}
        loading={loading}
        emptyMessage="No team members found"
        rowActions={rowActions}
      />

      {/* Invite User Modal */}
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        organizationId={organizationId}
        onSuccess={() => {
          setInviteModalOpen(false);
          fetchData();
        }}
      />

      {/* Edit User Role Modal */}
      {selectedMember && selectedMember.type === 'user' && (
        <EditUserRoleModal
          open={editRoleModalOpen}
          onClose={() => {
            setEditRoleModalOpen(false);
            setSelectedMember(null);
          }}
          user={{
            id: selectedMember.id,
            email: selectedMember.email,
            full_name: selectedMember.name,
            role: selectedMember.role as 'user' | 'admin',
          }}
          onSuccess={() => {
            setEditRoleModalOpen(false);
            setSelectedMember(null);
            fetchData();
          }}
        />
      )}

      {/* Remove User Dialog */}
      {selectedMember && selectedMember.type === 'user' && (
        <RemoveUserDialog
          open={removeUserDialogOpen}
          onClose={() => {
            setRemoveUserDialogOpen(false);
            setSelectedMember(null);
          }}
          user={{
            id: selectedMember.id,
            email: selectedMember.email,
            full_name: selectedMember.name,
          }}
          onSuccess={() => {
            setRemoveUserDialogOpen(false);
            setSelectedMember(null);
            fetchData();
          }}
        />
      )}
    </Stack>
  );
}
